/**
 * Pure client-side court suggestion: given a pending-allocation row + the
 * full schedule payload, recommend N courts that:
 *   1) Are FREE during the requested time slot (entire duration), and
 *   2) Best preserve same-sport homogeneity (we want to add this booking to
 *      a court that's already dominated by the same sport, to avoid manual
 *      net/floor switching by staff).
 *
 * Pure function — no React, no fetches.
 */
import { TIME_SLOTS } from './constants';

// Build a Set of "court#:hourIdx" keys that are already occupied by an
// AWAITING_PAYMENT or CONFIRMED booking on this date.
function buildOccupiedSet(assignedBookings) {
  const occupied = new Set();
  for (const b of assignedBookings || []) {
    const startStr = (b.time_slot || '').split('-')[0];
    const startHour = parseInt(startStr?.split(':')?.[0], 10);
    if (Number.isNaN(startHour)) continue;
    const dur = b.duration || 1;
    for (let h = 0; h < dur; h++) {
      const hourIdx = startHour + h - 8; // matches TIME_SLOTS indexing
      if (hourIdx < 0 || hourIdx >= TIME_SLOTS.length) continue;
      for (const c of b.courts || []) {
        if (c.court_number != null) {
          occupied.add(`${c.court_number}:${hourIdx}`);
        }
      }
    }
  }
  return occupied;
}

// Score a court for hosting a same-sport booking (higher = better).
function scoreCourt(courtNum, sport, homogeneity) {
  const info = homogeneity?.[String(courtNum)];
  if (!info) return 50; // unknown — treat as neutral
  const total = (info.sports?.badminton || 0) + (info.sports?.pickleball || 0);

  if (total === 0) return 50; // empty court — neutral; can become anything
  if (info.dominant_sport === sport && info.pct >= 100) return 100; // perfect
  if (info.dominant_sport === sport) return 70 + (info.pct - 50) * 0.5; // ~70-95
  if (info.dominant_sport && info.dominant_sport !== sport && info.pct >= 100) return 10; // disruptive
  return 30; // mixed leaning other
}

/**
 * @param {object} pending     — one entry from pending_allocation_bookings
 * @param {object} schedule    — full schedule payload from the API
 * @returns {object|null} { courts: [num, ...], reason: string } or null when no fit
 */
export default function suggestCourtsForPending(pending, schedule) {
  if (!pending || !schedule) return null;
  const sport = pending.primary_sport;
  if (!sport) return null;

  const startStr = (pending.time_slot || '').split('-')[0];
  const startHour = parseInt(startStr?.split(':')?.[0], 10);
  if (Number.isNaN(startHour)) return null;
  const duration = pending.duration || 1;

  const requiredHourIdxs = [];
  for (let h = 0; h < duration; h++) {
    const idx = startHour + h - 8;
    if (idx < 0 || idx >= TIME_SLOTS.length) return null; // outside grid
    requiredHourIdxs.push(idx);
  }

  const occupied = buildOccupiedSet(schedule.assigned_bookings);
  const courtsCount = schedule.location?.courts_count || 5;

  // Determine how many courts this pending booking actually needs
  const courtsNeeded =
    (pending.courts_requested || []).reduce(
      (s, c) => s + (c.quantity || 1),
      0
    ) || 1;

  const candidates = [];
  for (let cn = 1; cn <= courtsCount; cn++) {
    const free = requiredHourIdxs.every((hi) => !occupied.has(`${cn}:${hi}`));
    if (!free) continue;
    candidates.push({
      court: cn,
      score: scoreCourt(cn, sport, schedule.sport_homogeneity_per_court),
    });
  }

  if (candidates.length === 0) {
    return { courts: [], reason: 'No free courts in this time slot' };
  }
  if (candidates.length < courtsNeeded) {
    return {
      courts: candidates.map((c) => c.court),
      reason: `Only ${candidates.length} of ${courtsNeeded} needed courts are free`,
    };
  }

  // Sort by score desc, then by court number asc for determinism
  candidates.sort((a, b) => b.score - a.score || a.court - b.court);
  const top = candidates.slice(0, courtsNeeded);
  const sportLabel = sport === 'pickleball' ? 'pickleball' : 'badminton';
  const sample = schedule.sport_homogeneity_per_court?.[String(top[0].court)];
  let reason;
  if (!sample || (sample.sports?.badminton === 0 && sample.sports?.pickleball === 0)) {
    reason = `Empty so far — fresh start`;
  } else if (sample.dominant_sport === sport && sample.pct >= 100) {
    reason = `100% ${sportLabel} today`;
  } else if (sample.dominant_sport === sport) {
    reason = `${sample.pct}% ${sportLabel} today`;
  } else {
    reason = `Best available (mixed)`;
  }
  return { courts: top.map((c) => c.court), reason };
}
